import React, { FunctionComponent, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { EventContentArg } from '@fullcalendar/core'
import { format, parse } from 'date-fns'
import { isNil } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  CALENDAR_SETUP_DESCRIPTION_TEXT,
  CALENDAR_SETUP_HEADER_TEXT,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/messages'
import {
  BuiltEvent,
  CalendarProvider,
  EventModality,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { Title } from '../../atoms/title/Title'
import { Tooltip, TOOLTIP_TRIGGER_ICON } from '../../atoms/tooltip/Tooltip'
import { AppContext } from '../../context'
import { ProviderBookableCalendarSlotTarget } from '../../molecules/providerBookableCalendarSlotTarget/ProviderBookableCalendarSlotTarget'
import { ToggleSwitch } from '../../molecules/toggleSwitch/ToggleSwitch'
import { ConfirmationModal } from '../../organisms/confirmationModal/ConfirmationModal'
import {
  ProviderBookableCalendar,
  ProviderBookableCalendarRef,
} from '../../organisms/providerBookableCalendar/ProviderBookableCalendar.web'
import { ProviderCalendarDaySlotSelectionModal } from '../../organisms/providerCalendarSlotSelectionModal/ProviderCalendarDaySlotSelectionModal'
import './style.css'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ProviderBookableCalendarSetupProps = {
  timeZone?: string
  /** Callback when timezone is changed via the selector. When provided, shows the timezone dropdown. */
  onTimeZoneChange?: (timeZone: string) => void
  onSave?: (events: BuiltEvent[], slotMinTime: string, slotMaxTime: string) => void
  onCancel?: () => void
  isSaving?: boolean
  isEditing?: boolean
  initialEvents?: BuiltEvent[]
  bookableTarget: number
  bookableMax: number
  onChange?: (events: { startTime: string; endTime: string }[]) => void
  hidePerformanceMetrics?: boolean
  hybridBookingMode?: boolean
  calendarProvider?: CalendarProvider
  showExpandableWorkingHours?: boolean
  expandedWorkingHours?: boolean
  flexibleAvailabilityWindows?: boolean
  inPersonPreference?: boolean
  defaultVideoPlatform?: VIDEO_PLATFORM | null
  splitVideoOptions?: boolean
  providerAddresses?: Address[] | null
  showModalitySelection?: boolean
  supportsOnOffPlatformVideo?: boolean
  videoPreference?: boolean
  isNPSession?: boolean
  expandedStartTime?: string
  expandedEndTime?: string
  /** Override display start time independently of working hours (e.g., '00:00:00' for 24h view) */
  displayStartTime?: string
  /** Override display end time independently of working hours (e.g., '24:00:00' for 24h view) */
  displayEndTime?: string
  /** Days to hide from calendar (0=Sunday, 6=Saturday) */
  hiddenDays?: number[]
}

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundSecondary,
  alignItems: 'center',
}))

const Header = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  padding: `0 ${theme.spacing['24px']}`,
  width: '100%',
  borderBottomWidth: '1px',
  borderBottomColor: theme.colors.borderDefault,
}))

const HeaderRow = styled.View<{ theme: ThemeType; bottomBorder?: boolean }>(({ theme, bottomBorder }) => ({
  flexDirection: 'row',
  padding: theme.spacing['24px'],
  justifyContent: 'space-between',
  alignItems: 'center',
  ...(bottomBorder && {
    borderBottomWidth: '1px',
    borderBottomColor: theme.colors.borderDefault,
  }),
}))

const CalendarContainer = styled.View({
  overflow: 'scroll',
  width: '100%',
  height: 'calc(100vh - 114px)',
})

const HeaderText = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['8px'],
}))

const HeaderBodyText = styled(BodyText)(({ theme }) => ({
  maxWidth: '850px',
  color: theme.colors.textSecondary,
}))

const Row = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const HeaderSection = styled(Row)(({ theme }) => ({
  gap: theme.spacing['32px'],
}))

const ButtonGroup = styled(Row)(({ theme }) => ({
  gap: theme.spacing['16px'],
}))

const WorkingHoursSection = styled(Row)(({ theme }) => ({
  borderLeftWidth: '1px',
  borderLeftColor: theme.colors.borderDefault,
  gap: theme.spacing['8px'],
  paddingLeft: theme.spacing['32px'],
}))

// Default expanded hours for providers with the toggle
const DEFAULT_EXPANDED_START = '04:00:00'
const DEFAULT_EXPANDED_END = '23:00:00'
// Default working hours when toggle is OFF
const DEFAULT_WORKING_HOURS_START = '07:00:00'
const DEFAULT_WORKING_HOURS_END = '22:00:00'

export const ProviderBookableCalendarSetup: FunctionComponent<ProviderBookableCalendarSetupProps> = ({
  timeZone = 'America/Los_Angeles',
  onTimeZoneChange,
  onSave,
  onCancel,
  isSaving = false,
  isEditing = false,
  initialEvents,
  bookableTarget,
  bookableMax,
  onChange,
  hidePerformanceMetrics,
  hybridBookingMode,
  calendarProvider,
  showExpandableWorkingHours = false,
  flexibleAvailabilityWindows,
  defaultVideoPlatform,
  inPersonPreference,
  splitVideoOptions,
  providerAddresses,
  showModalitySelection,
  supportsOnOffPlatformVideo,
  videoPreference,
  isNPSession,
  expandedStartTime,
  expandedEndTime,
  displayStartTime,
  displayEndTime,
  hiddenDays,
}) => {
  const calendarRef = useRef<ProviderBookableCalendarRef>(null)
  const { colors } = useTheme()
  const { formatMessage } = useIntl()
  const { isExpandableWorkingHoursEnabled, isInProductCalendarAddModalEnabled } = useContext(AppContext)
  const [events, setEvents] = useState<BuiltEvent[]>(initialEvents ?? [])
  const [showAddEventModal, setShowAddEventModal] = useState(false)
  const [modalAddedEvents, setModalAddedEvents] = useState<
    {
      startTime: string
      endTime: string
      id?: string
      modality?: EventModality | null
      booking_type?: string[] | null
    }[]
  >([])
  const [eventToDelete, setEventToDelete] = useState<EventContentArg | null>(null)
  const [eventToEdit, setEventToEdit] = useState<EventContentArg | null>(null)
  const effectiveExpandedStart = expandedStartTime ?? DEFAULT_EXPANDED_START
  const effectiveExpandedEnd = expandedEndTime ?? DEFAULT_EXPANDED_END
  // Only track toggle state when the toggle is actually shown
  const [expandedWorkingHours, setExpandedWorkingHours] = useState(
    showExpandableWorkingHours && calendarProvider?.daily_start === effectiveExpandedStart,
  )
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false)
  const [rangeToAdd, setRangeToAdd] = useState<{
    start: string
    end: string
    startLocal: string
    endLocal: string
  } | null>(null)
  // For providers with explicit hours from role config (BEND providers via dailyStart/dailyEnd,
  // or DA_THERAPIST via expandedStartTime/expandedEndTime), use them directly when no toggle is shown.
  // For providers with the expandable hours toggle (showExpandableWorkingHours=true):
  // - Toggle ON (expandedWorkingHours=true): use expanded hours
  // - Toggle OFF: use default working hours (7am-10pm)
  // For providers without toggle AND without explicit hours (e.g., COACH):
  // - Fall back to calendarProvider values or defaults
  const slotMinTime = useMemo(() => {
    // If role config provides explicit hours (BEND via dailyStart, or others via expandedStartTime)
    // AND no toggle is shown, use them directly
    if (!showExpandableWorkingHours && expandedStartTime) {
      return expandedStartTime
    }
    // Regular providers with toggle, or those without explicit hours
    return expandedWorkingHours
      ? effectiveExpandedStart
      : (calendarProvider?.daily_start ?? DEFAULT_WORKING_HOURS_START)
  }, [
    expandedStartTime,
    expandedWorkingHours,
    effectiveExpandedStart,
    calendarProvider?.daily_start,
    showExpandableWorkingHours,
  ])
  const slotMaxTime = useMemo(() => {
    if (!showExpandableWorkingHours && expandedEndTime) {
      return expandedEndTime
    }
    return expandedWorkingHours ? effectiveExpandedEnd : (calendarProvider?.daily_end ?? DEFAULT_WORKING_HOURS_END)
  }, [
    expandedEndTime,
    expandedWorkingHours,
    effectiveExpandedEnd,
    calendarProvider?.daily_end,
    showExpandableWorkingHours,
  ])

  const expandedStartLabel = format(parse(effectiveExpandedStart, 'HH:mm:ss', new Date()), 'h a')
  const expandedEndLabel = format(parse(effectiveExpandedEnd, 'HH:mm:ss', new Date()), 'h a')

  useEffect(() => {
    onChange?.(events)
  }, [events, onChange])

  useEffect(() => {
    calendarRef?.current?.reset()
  }, [])

  const setEventTimes = (modalEvents: BuiltEvent[]) => {
    const event = modalEvents[0]
    if (!!event.modality) {
      eventToEdit?.event.setExtendedProp('modality_availability', event.modality)
    }
    if (event.booking_type !== undefined) {
      eventToEdit?.event.setExtendedProp('booking_type', event.booking_type)
    }
    if (flexibleAvailabilityWindows || isNPSession) {
      eventToEdit?.event.setDates(event.startTime, event.endTime)
    } else {
      eventToEdit?.event.setStart(event.startTime, { maintainDuration: true })
    }
    setEventToEdit(null)
  }

  return (
    <Container>
      <Header>
        <HeaderRow bottomBorder>
          <HeaderText>
            <Title
              size={TitleSize.LARGE}
              text={
                <FormattedMessage
                  {...(hybridBookingMode
                    ? isEditing
                      ? CALENDAR_SETUP_HEADER_TEXT.HYBRID_EDIT
                      : CALENDAR_SETUP_HEADER_TEXT.HYBRID_SETUP
                    : isEditing
                      ? CALENDAR_SETUP_HEADER_TEXT.BOOKABLE_EDIT
                      : CALENDAR_SETUP_HEADER_TEXT.BOOKABLE_SETUP)}
                />
              }
            />
            {!isEditing && (
              <HeaderBodyText
                text={
                  <FormattedMessage
                    {...(hybridBookingMode
                      ? CALENDAR_SETUP_DESCRIPTION_TEXT.HYBRID
                      : CALENDAR_SETUP_DESCRIPTION_TEXT.BOOKABLE)}
                  />
                }
                color={colors.textSecondary}
              />
            )}
          </HeaderText>
          <HeaderSection>
            <ButtonGroup>
              <TertiaryButton
                testID={tID('ProviderBookableCalendarSetup-cancel-button')}
                text={<FormattedMessage defaultMessage='Cancel' description='Button to cancel calendar setup' />}
                onPress={() => onCancel?.()}
              />
              <PrimaryButton
                loading={isSaving}
                testID={tID('ProviderBookableCalendarSetup-save-button')}
                text={<FormattedMessage defaultMessage='Save' description='Button to save calendar setup' />}
                onPress={() => onSave?.(events, slotMinTime, slotMaxTime)}
                disabled={saveButtonDisabled}
              />
            </ButtonGroup>
          </HeaderSection>
        </HeaderRow>
        <HeaderRow>
          <HeaderSection>
            {!hidePerformanceMetrics && (
              <ProviderBookableCalendarSlotTarget
                target={bookableTarget}
                max={bookableMax}
                current={events.length}
                showWarningBelowTarget={isEditing}
              />
            )}
            {isInProductCalendarAddModalEnabled && (
              <SecondaryButton
                testID={tID('ProviderBookableCalendarSetup-add-button')}
                text={
                  <FormattedMessage defaultMessage='Add' description='Button to add availability to calendar setup' />
                }
                onPress={() => {
                  setShowAddEventModal(true)
                }}
              />
            )}
            {isExpandableWorkingHoursEnabled && showExpandableWorkingHours && (
              <WorkingHoursSection>
                <BodyText
                  text={
                    <FormattedMessage
                      defaultMessage='Expanded working hours'
                      description='Text describing the toggle for expanded working hours'
                    />
                  }
                  color={colors.textPrimary}
                />
                <Tooltip
                  hoverEnabled
                  content={formatMessage(
                    {
                      defaultMessage:
                        'Extends potential booking timeframe to {expandedStartLabel} - {expandedEndLabel} in your timezone',
                      description: 'Tooltip describing what expanded working hours are',
                    },
                    { expandedStartLabel, expandedEndLabel },
                  )}
                  triggerConfig={{
                    icon: TOOLTIP_TRIGGER_ICON.INFO,
                    size: 24,
                    fillColor: colors.textSecondary,
                  }}
                />
                <ToggleSwitch
                  isOn={expandedWorkingHours}
                  testID={tID(`ProviderBookableCalendarSetup-expandedWorkingHoursToggle-`)}
                  onPress={() => {
                    calendarRef?.current?.changeWorkingHours({
                      startTime: expandedWorkingHours ? DEFAULT_WORKING_HOURS_START : effectiveExpandedStart,
                      endTime: expandedWorkingHours ? DEFAULT_WORKING_HOURS_END : effectiveExpandedEnd,
                    })
                    setExpandedWorkingHours(!expandedWorkingHours)
                  }}
                  accessibilityLabel={formatMessage(
                    {
                      defaultMessage:
                        'Expand working hours toggle {expandedWorkingHours, select, true {on} other {off}}',
                      description: 'Screen reader text for expanded working hours toggle',
                    },
                    { expandedWorkingHours },
                  )}
                />
              </WorkingHoursSection>
            )}
          </HeaderSection>
        </HeaderRow>
      </Header>
      <CalendarContainer tabIndex={0} testID={tID('ProviderBookableCalendarSetup-calendar-container')}>
        <ProviderBookableCalendar
          ref={calendarRef}
          timeZone={timeZone}
          onTimeZoneChange={onTimeZoneChange}
          providerTimeZone={calendarProvider?.timezone}
          onEventsChanged={setEvents}
          initialEvents={initialEvents}
          modalAddedEvents={modalAddedEvents}
          setEventToDelete={setEventToDelete}
          setEventToEdit={(arg) => {
            setShowAddEventModal(true)
            setEventToEdit(arg)
          }}
          setRangeToAdd={setRangeToAdd}
          setShowAddEventModal={setShowAddEventModal}
          showModalitySelection={showModalitySelection}
          supportsOnOffPlatformVideo={supportsOnOffPlatformVideo}
          slotMinTime={slotMinTime}
          slotMaxTime={slotMaxTime}
          hybridBookingMode={hybridBookingMode}
          expandedWorkingHours={expandedWorkingHours}
          setSaveButtonDisabled={setSaveButtonDisabled}
          showExpandableWorkingHours={showExpandableWorkingHours}
          isNPSession={isNPSession}
          expandedStartTime={displayStartTime ?? effectiveExpandedStart}
          expandedEndTime={displayEndTime ?? effectiveExpandedEnd}
          hiddenDays={hiddenDays}
          flexibleAvailabilityWindows={flexibleAvailabilityWindows}
        />
      </CalendarContainer>
      <ProviderCalendarDaySlotSelectionModal
        addresses={providerAddresses}
        currentEvents={events}
        defaultVideoPlatform={defaultVideoPlatform}
        eventToEdit={eventToEdit}
        flexibleAvailabilityWindows={flexibleAvailabilityWindows}
        inPersonPreference={inPersonPreference}
        splitVideoOptions={splitVideoOptions}
        onConfirmationButtonPress={(modalEvents: BuiltEvent[]) => {
          setRangeToAdd(null)
          eventToEdit ? setEventTimes(modalEvents) : setModalAddedEvents(modalEvents)
        }}
        onRequestClose={() => {
          setShowAddEventModal(false)
          setRangeToAdd(null)
          setEventToEdit(null)
        }}
        rangeToAdd={rangeToAdd}
        setShowAddEventModal={setShowAddEventModal}
        showAddEventModal={showAddEventModal}
        showModalitySelection={showModalitySelection}
        slotMinTime={slotMinTime}
        slotMaxTime={slotMaxTime}
        videoPreference={videoPreference}
        isNPSession={isNPSession}
        hiddenDays={hiddenDays}
      />
      <ConfirmationModal
        visible={!isNil(eventToDelete)}
        onRequestClose={() => setEventToDelete(null)}
        onConfirmationButtonPress={() => {
          !isNil(eventToDelete) && eventToDelete.event.remove()
          setEventToDelete(null)
          if (eventToDelete?.event.extendedProps.outsideBookingHours) {
            calendarRef.current?.decreaseBeyondBookingHoursEventCount()
          }
        }}
        modalTitle={
          <FormattedMessage
            defaultMessage='Delete availability?'
            description='Title of modal to delete recurring availability in calendar template'
          />
        }
        cancelButtonText={<FormattedMessage defaultMessage='Cancel' description='Cancel button' />}
        confirmationButtonText={<FormattedMessage defaultMessage='Delete' description='Add availability button' />}
        modalContents={
          <BodyText
            text={
              <FormattedMessage
                defaultMessage='Clients will not be able to book at this time.'
                description='Body text for delete confirmation modal'
              />
            }
          />
        }
        testID={'DeleteTemplateAvailabilityModal'}
        singleLineButtons
      />
    </Container>
  )
}
